import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Calendar,
  LayoutDashboard,
  FileText,
  Settings,
  LogOut,
  Bell,
  Menu,
  User,
  Check,
  BookOpen,
  Users,
  CreditCard,
} from "lucide-react";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import UpdateNotification, { VersionBadge } from "@/components/UpdateNotification";

export default function Layout({ children }) {
  const { user, logout, token, settings } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  const baseNavItems = [
    { path: "/dashboard", label: "Home", icon: LayoutDashboard },
    { path: "/requests", label: "Richieste", icon: FileText },
    { path: "/calendar", label: "Calendario", icon: Calendar },
    { path: "/settings", label: "Impostazioni", icon: Settings },
    { path: "/instructions", label: "Istruzioni", icon: BookOpen },
  ];

  // Build navigation items based on role
  let navItems = [...baseNavItems];
  
  // Add "Dipendenti" for titolare and amministrativo
  if (user?.ruolo === "titolare" || user?.ruolo === "amministrativo") {
    navItems = [
      ...baseNavItems.slice(0, 2),
      { path: "/users", label: "Dipendenti", icon: Users },
      ...baseNavItems.slice(2)
    ];
  }
  
  // Add "Abbonamento" only for titolare (after Impostazioni)
  if (user?.ruolo === "titolare") {
    const settingsIndex = navItems.findIndex(item => item.path === "/settings");
    navItems.splice(settingsIndex + 1, 0, { path: "/abbonamento", label: "Abbonamento", icon: CreditCard });
  }

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const fetchNotifications = async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        axios.get(`${API}/notifications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/notifications/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setNotifications(notifRes.data.slice(0, 10));
      setUnreadCount(countRes.data.count);
    } catch (error) {
      console.error("Failed to fetch notifications");
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchNotifications();
    } catch (error) {
      console.error("Failed to mark notification as read");
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchNotifications();
    } catch (error) {
      console.error("Failed to mark all as read");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const NavLink = ({ item, mobile = false }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    return (
      <Link
        to={item.path}
        onClick={() => mobile && setMobileOpen(false)}
        className={`flex items-center gap-3 px-4 py-3 rounded-xl sidebar-link transition-all duration-200 ${
          isActive 
            ? "text-white shadow-lg" 
            : "text-slate-300 hover:text-white hover:bg-white/10"
        }`}
        style={isActive ? {
          background: `linear-gradient(to right, var(--nav-active-from), var(--nav-active-to))`,
          boxShadow: `0 10px 15px -3px var(--nav-active-shadow)`
        } : {}}
        data-testid={`nav-${item.path.slice(1)}`}
      >
        <Icon className={`h-5 w-5 ${isActive ? "text-white" : ""}`} />
        <span className="font-medium">{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-1 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 px-4 py-6">
          {/* Logo */}
          <div className="flex items-center gap-3 px-2 mb-8">
            {settings.logo_base64 ? (
              <img src={settings.logo_base64} alt="Logo" className="h-16 w-auto object-contain rounded-lg" />
            ) : (
              <div className="h-16 w-16 rounded-xl flex items-center justify-center shadow-lg" style={{ background: `linear-gradient(to bottom right, var(--theme-500), var(--theme-600))` }}>
                <Calendar className="h-8 w-8 text-white" />
              </div>
            )}
            <div>
              <h2 className="text-white font-bold text-sm truncate max-w-[140px]">{settings.nome_azienda}</h2>
              <p className="text-slate-400 text-xs">Ferie e Permessi</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1">
            {navItems.map((item) => (
              <NavLink key={item.path} item={item} />
            ))}
          </nav>

          {/* User info */}
          <div className="border-t border-slate-800 pt-4 mt-4">
            <div className="flex items-center gap-3 px-2">
              <div className="h-10 w-10 bg-slate-700 rounded-full flex items-center justify-center">
                <User className="h-5 w-5 text-slate-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">
                  {user?.nome} {user?.cognome}
                </p>
                <p className="text-slate-400 text-xs capitalize">{user?.ruolo}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full mt-3 text-slate-300 hover:text-white hover:bg-slate-800 justify-start"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Esci
            </Button>
            {/* Version badge */}
            <div className="mt-3 px-2">
              <VersionBadge className="text-slate-500" />
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 glass-header border-b border-slate-200">
        <div className="flex items-center justify-between px-4 py-3">
          {/* Logo and Company Name - Always visible on mobile */}
          <div className="flex items-center gap-3">
            {settings.logo_base64 ? (
              <img src={settings.logo_base64} alt="Logo" className="h-14 w-auto object-contain rounded-lg" />
            ) : (
              <div className="h-14 w-14 rounded-xl flex items-center justify-center shadow-sm" style={{ background: `linear-gradient(to bottom right, var(--theme-500), var(--theme-600))` }}>
                <Calendar className="h-7 w-7 text-white" />
              </div>
            )}
            <div>
              <h2 className="font-bold text-sm text-slate-900 truncate max-w-[140px]">{settings.nome_azienda}</h2>
              <p className="text-slate-500 text-xs">Ferie e Permessi</p>
            </div>
          </div>

          {/* Right side: Notifications + Menu Button */}
          <div className="flex items-center gap-1">
            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="relative rounded-xl h-12 w-12"
                  style={{ backgroundColor: 'var(--theme-100)' }}
                  data-testid="notifications-btn-mobile"
                >
                  <Bell className="h-6 w-6" style={{ color: 'var(--theme-700)' }} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="font-semibold">Notifiche</span>
                  {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs">
                      Segna tutte come lette
                    </Button>
                  )}
                </div>
                <DropdownMenuSeparator />
                {notifications.length === 0 ? (
                  <div className="px-3 py-6 text-center text-slate-500 text-sm">
                    Nessuna notifica
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <DropdownMenuItem key={notif.id} className="flex flex-col items-start gap-1 p-3 cursor-pointer" onClick={() => markAsRead(notif.id)}>
                      <div className="flex items-center gap-2 w-full">
                        <span className={`font-medium text-sm ${!notif.letto ? "text-slate-900" : "text-slate-500"}`}>
                          {notif.titolo}
                        </span>
                        {!notif.letto && <div className="h-2 w-2 rounded-full" style={{ backgroundColor: 'var(--theme-500)' }}></div>}
                      </div>
                      <span className="text-xs text-slate-400">{notif.messaggio}</span>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Menu Button */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="rounded-xl h-12 w-12"
                  style={{ backgroundColor: 'var(--theme-600)' }}
                  data-testid="mobile-menu-btn"
                >
                  <Menu className="h-7 w-7 text-white" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 bg-slate-900 p-0 border-0">
                <div className="flex flex-col h-full px-4 py-6">
                  {/* User info at top of menu */}
                  <div className="flex items-center gap-3 px-2 mb-6 pb-4 border-b border-slate-800">
                    <div className="h-10 w-10 bg-slate-700 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-slate-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">
                        {user?.nome} {user?.cognome}
                      </p>
                      <p className="text-slate-400 text-xs capitalize">{user?.ruolo}</p>
                    </div>
                  </div>
                  
                  {/* Navigation */}
                  <nav className="flex-1 space-y-1">
                    {navItems.map((item) => (
                      <NavLink key={item.path} item={item} mobile />
                    ))}
                  </nav>
                  
                  {/* Logout button */}
                  <Button
                    variant="ghost"
                    onClick={handleLogout}
                    className="w-full mt-4 text-slate-300 hover:text-white hover:bg-slate-800 justify-start"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Esci
                  </Button>
                  {/* Version badge mobile */}
                  <div className="mt-3 px-2">
                    <VersionBadge className="text-slate-500" />
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden lg:block fixed top-0 left-64 right-0 z-40 glass-header border-b border-slate-200">
        <div className="flex items-center justify-end px-6 py-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="relative rounded-xl h-11 w-11"
                style={{ backgroundColor: 'var(--theme-100)' }}
                data-testid="desktop-notifications-btn"
              >
                <Bell className="h-5 w-5" style={{ color: 'var(--theme-700)' }} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="flex items-center justify-between px-3 py-2">
                <span className="font-semibold">Notifiche</span>
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs">
                    Segna tutte come lette
                  </Button>
                )}
              </div>
              <DropdownMenuSeparator />
              {notifications.length === 0 ? (
                <div className="px-3 py-6 text-center text-slate-500 text-sm">
                  Nessuna notifica
                </div>
              ) : (
                notifications.map((notif) => (
                  <DropdownMenuItem key={notif.id} className="flex flex-col items-start gap-1 p-3 cursor-pointer" onClick={() => markAsRead(notif.id)}>
                    <div className="flex items-center gap-2 w-full">
                      <span className={`font-medium text-sm ${!notif.letto ? "text-slate-900" : "text-slate-500"}`}>
                        {notif.titolo}
                      </span>
                      {!notif.letto && <div className="h-2 w-2 rounded-full" style={{ backgroundColor: 'var(--theme-500)' }}></div>}
                    </div>
                    <span className="text-xs text-slate-500 line-clamp-2">{notif.messaggio}</span>
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main content */}
      <main className="lg:pl-64 pt-16 lg:pt-14 min-h-screen" style={{ background: `linear-gradient(to bottom right, rgb(248, 250, 252), var(--bg-gradient-via), var(--bg-gradient-to))` }}>
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
      
      {/* Subscription limit warning banner */}
      <SubscriptionBanner />
      
      {/* Update notification */}
      <UpdateNotification />
    </div>
  );
}
